import { navigateTo, useRouter, useRuntimeConfig } from '#imports'
import { computed, ref } from 'vue'

import { MarketCountryCode } from '@backmarket/http-api'
import type { CartItem } from '@backmarket/http-api/src/api-specs-checkout/cart/cart.types'
import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { useClientOnlyComputedBreakpoint } from '@backmarket/utils/composables/useClientOnlyComputedBreakpoint'
import { openModal } from '@ds/components/ModalBase'

import { ROUTES } from '~/scopes/auth/route-names'
import useHandleUnauthorizedUser from '~/scopes/checkout/composables/useHandleUnauthorizedUser'
import { MODAL_NAMES } from '~/scopes/checkout/config/constants'
import { CHECKOUT } from '~/scopes/checkout/routes-names'
import { scrollToFirstItemWithCompliancyError } from '~/scopes/checkout/utils/cart'
import {
  hasInsuranceSelected,
  hasOfferCompliancyError,
} from '~/scopes/insurance/utils/insuranceOffers'

// Since we are only using this composable for the whole cart, we are in using this ref among all components
const hasBeenSubmitted = ref(false)

export function useGoNextStep() {
  const tracking = useTracking()
  const runtimeConfig = useRuntimeConfig()
  const breakpoint = useClientOnlyComputedBreakpoint()
  const {
    market: { countryCode },
  } = useMarketplace()

  const router = useRouter()
  const userStore = useUserStore()

  const { handleUnauthorizedUser } = useHandleUnauthorizedUser()
  const experiments = useExperiments()

  const isSinglePageAddressesEnabled = computed(() => {
    return experiments['experiment.checkoutOnePageAddress'] === 'onePageAddress'
  })

  const nextStep = (
    items: CartItem[],
    isShippable: boolean,
    { trackClick = true }: { trackClick?: boolean } = {},
  ) => {
    const firstItemWithCompliancyError = items.find(({ insuranceOffers }) =>
      insuranceOffers.some(hasOfferCompliancyError),
    )

    if (trackClick) {
      tracking.trackClick({
        name: 'go_checkout',
        zone: 'cart',
        hasCompliancyError: Boolean(firstItemWithCompliancyError),
      })
    }

    hasBeenSubmitted.value = true

    if (firstItemWithCompliancyError) {
      scrollToFirstItemWithCompliancyError(
        breakpoint.value,
        firstItemWithCompliancyError.listingId,
      )

      // block nextStep if compliancy is not checked
      return
    }

    // Catch Up Modal
    const itemsWithSelectableInsurances = items.filter(
      ({ insuranceOffers }) => !hasInsuranceSelected(insuranceOffers),
    )

    const hasCartOnlyOneInsuranceSelectable =
      itemsWithSelectableInsurances.length === 1

    const showCatchupModal =
      sessionStorage.getItem('showCatchupModal') === 'true'

    const isCatchUpModalEnabled =
      !runtimeConfig.public.KILL_INSURANCE_CATCHUP_MODAL &&
      showCatchupModal &&
      countryCode !== MarketCountryCode.JP

    if (hasCartOnlyOneInsuranceSelectable && isCatchUpModalEnabled) {
      hasBeenSubmitted.value = false
      openModal(MODAL_NAMES.CATCH_UP_INSURANCE_OFFER)

      // block nextStep if modal is opened
      return
    }

    // eslint-disable-next-line no-nested-ternary
    const nextRouteName = isSinglePageAddressesEnabled.value
      ? CHECKOUT.ADDRESSES
      : isShippable
        ? CHECKOUT.ADDRESS_CONFIRMATION
        : CHECKOUT.SHIPPING_ADDRESS

    try {
      if (userStore.isUserAuthenticated) {
        // check auth state and proceed
        navigateTo({
          name: nextRouteName,
        })
      } else {
        const { href } = router.resolve({ name: nextRouteName })

        navigateTo({
          name: ROUTES.AUTH.LOGIN,
          query: {
            next: href,
            bm_journey: 'checkout',
          },
        })
      }
    } catch (error) {
      handleUnauthorizedUser(
        error as Record<string, unknown>,
        '[Checkout] Unhandled error going to the next step',
      )
    }
  }

  return {
    nextStep,
    hasBeenSubmitted,
  }
}
